/**
 * Email Variable Utility Functions
 *
 * Helper functions for working with email variables in content.
 * Handles extraction, validation, and manipulation of {{variable}} tokens.
 *
 * Phase 1, Task 1.5
 * Date: 2025-10-21
 */

import {
  EmailVariableCategory,
  getAllVariables,
  getVariableByTemplate,
  isKnownVariable,
  isDynamicVariable as isVariableDynamic,
  isRequiredVariable,
  getRequiredVariables,
  getVariableCategory as getVarCategory
} from '@/components/email/editor/variables/types';

/**
 * Validation result interface
 */
export interface ValidationResult {
  valid: boolean;
  missing: string[];
  unknown: string[];
  unreplacedPlaceholders: string[];
}

/**
 * Extract all variables from email content
 * Matches {{variable}} pattern
 *
 * @param content - Email HTML or text content
 * @returns Array of unique variables found (with braces)
 *
 * @example
 * extractVariablesFromContent('Hi {{firstName}}, visit {{orderUrl}}')
 * // Returns: ['{{firstName}}', '{{orderUrl}}']
 */
export function extractVariablesFromContent(content: string): string[] {
  if (!content || typeof content !== 'string') {
    return [];
  }

  // Match {{variable}} pattern (double braces)
  const regex = /{{([a-zA-Z_][a-zA-Z0-9_]*)}}/g;
  const matches = content.match(regex);

  if (!matches) {
    return [];
  }

  // Return unique variables
  return [...new Set(matches)];
}

/**
 * Validate that all required variables are present
 * Currently only checks for {{unsubscribeUrl}} (CAN-SPAM requirement)
 *
 * @param content - Email HTML or text content
 * @returns Validation result with missing required variables
 *
 * @example
 * validateRequiredVariables('Email without unsubscribe')
 * // Returns: { valid: false, missing: ['{{unsubscribeUrl}}'] }
 */
export function validateRequiredVariables(content: string): ValidationResult {
  const requiredVars = getRequiredVariables();
  const extractedVars = extractVariablesFromContent(content);

  const missing = requiredVars
    .filter(req => !extractedVars.includes(req.variable))
    .map(req => req.variable);

  // Check for unreplaced placeholders (SMS-style or manual)
  const placeholderRegex = /\[(CODE|ITEM|DISCOUNT)\]/g;
  const placeholders = content.match(placeholderRegex) || [];
  const unreplacedPlaceholders = [...new Set(placeholders)];

  // Check for unknown variables (not in our definition)
  const unknown = extractedVars.filter(v => !isKnownVariable(v));

  return {
    valid: missing.length === 0,
    missing,
    unknown,
    unreplacedPlaceholders
  };
}

/**
 * Get variable category from template string
 *
 * @param variable - Variable template string with braces
 * @returns Category or null if unknown
 *
 * @example
 * getVariableCategory('{{firstName}}')
 * // Returns: 'customer'
 */
export function getVariableCategory(variable: string): EmailVariableCategory | null {
  return getVarCategory(variable);
}

/**
 * Check if variable is dynamic (requires runtime resolution)
 *
 * @param variable - Variable template string
 * @returns True if variable is dynamic
 *
 * @example
 * isDynamicVariable('{{preferredRestaurantOrderingUrl}}')
 * // Returns: true
 */
export function isDynamicVariable(variable: string): boolean {
  return isVariableDynamic(variable);
}

/**
 * Format variable for display (remove braces)
 *
 * @param variable - Variable with braces
 * @returns Variable without braces
 *
 * @example
 * formatVariableForDisplay('{{firstName}}')
 * // Returns: 'firstName'
 */
export function formatVariableForDisplay(variable: string): string {
  return variable.replace(/{{|}}/g, '');
}

/**
 * Check if content contains any variables
 *
 * @param content - Email content
 * @returns True if at least one variable found
 */
export function containsVariables(content: string): boolean {
  return extractVariablesFromContent(content).length > 0;
}

/**
 * Count variables in content
 *
 * @param content - Email content
 * @returns Object with total count and count by category
 */
export function countVariables(content: string): {
  total: number;
  byCategory: Record<EmailVariableCategory, number>;
  dynamic: number;
  required: number;
} {
  const variables = extractVariablesFromContent(content);

  const byCategory: Record<EmailVariableCategory, number> = {
    customer: 0,
    activity: 0,
    restaurant: 0,
    dynamic: 0,
    campaign: 0,
    system: 0
  };

  let dynamicCount = 0;
  let requiredCount = 0;

  variables.forEach(variable => {
    const category = getVariableCategory(variable);
    if (category) {
      byCategory[category]++;
    }

    if (isDynamicVariable(variable)) {
      dynamicCount++;
    }

    if (isRequiredVariable(variable)) {
      requiredCount++;
    }
  });

  return {
    total: variables.length,
    byCategory,
    dynamic: dynamicCount,
    required: requiredCount
  };
}

/**
 * Group variables by category
 *
 * @param variables - Array of variable template strings
 * @returns Variables grouped by category
 */
export function groupVariablesByCategory(variables: string[]): Record<EmailVariableCategory, string[]> {
  const grouped: Record<EmailVariableCategory, string[]> = {
    customer: [],
    activity: [],
    restaurant: [],
    dynamic: [],
    campaign: [],
    system: []
  };

  variables.forEach(variable => {
    const category = getVariableCategory(variable);
    if (category) {
      grouped[category].push(variable);
    }
  });

  return grouped;
}

/**
 * Get user-friendly error messages for validation
 *
 * @param validation - Validation result
 * @returns Array of error/warning messages
 */
export function getValidationMessages(validation: ValidationResult): {
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Missing required variables are errors (blocks send)
  if (validation.missing.length > 0) {
    errors.push(
      `Missing required variable${validation.missing.length > 1 ? 's' : ''}: ${validation.missing.join(', ')}. ` +
      `This is required for CAN-SPAM compliance.`
    );
  }

  // Unknown variables are warnings (doesn't block send, but may not work)
  if (validation.unknown.length > 0) {
    warnings.push(
      `Unknown variable${validation.unknown.length > 1 ? 's' : ''} found: ${validation.unknown.join(', ')}. ` +
      `These may not be replaced when the email is sent.`
    );
  }

  // Unreplaced placeholders are warnings
  if (validation.unreplacedPlaceholders.length > 0) {
    warnings.push(
      `Unreplaced placeholder${validation.unreplacedPlaceholders.length > 1 ? 's' : ''}: ${validation.unreplacedPlaceholders.join(', ')}. ` +
      `Please replace these with actual values using the promo code selector.`
    );
  }

  return { errors, warnings };
}

/**
 * Check if a specific variable is present in content
 *
 * @param content - Email content
 * @param variable - Variable to check for
 * @returns True if variable is present
 */
export function hasVariable(content: string, variable: string): boolean {
  return content.includes(variable);
}

/**
 * Replace a variable in content with a value
 * Useful for preview or testing
 *
 * @param content - Email content
 * @param variable - Variable to replace
 * @param value - Value to replace with
 * @returns Content with variable replaced
 */
export function replaceVariable(content: string, variable: string, value: string): string {
  // Escape special regex characters in variable
  const escapedVar = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedVar, 'g');
  return content.replace(regex, value);
}

/**
 * Get variable metadata for display
 *
 * @param variable - Variable template string
 * @returns Metadata object or null
 */
export function getVariableMetadata(variable: string) {
  const varDef = getVariableByTemplate(variable);

  if (!varDef) {
    return null;
  }

  return {
    name: varDef.name,
    description: varDef.description,
    category: varDef.category,
    example: varDef.example,
    isDynamic: isDynamicVariable(variable),
    isRequired: isRequiredVariable(variable),
    note: varDef.note
  };
}

/**
 * Preview variable replacement with example values
 * Useful for showing users what the email will look like
 *
 * @param content - Email content with variables
 * @returns Content with variables replaced by example values
 */
export function previewWithExamples(content: string): string {
  let preview = content;
  const variables = extractVariablesFromContent(content);

  variables.forEach(variable => {
    const varDef = getVariableByTemplate(variable);
    if (varDef && varDef.example) {
      preview = replaceVariable(preview, variable, varDef.example);
    }
  });

  return preview;
}

/**
 * Sanitize content to prevent XSS when displaying variables
 * Note: This is basic sanitization. Use a proper library for production.
 *
 * @param content - Raw content
 * @returns Sanitized content
 */
export function sanitizeForPreview(content: string): string {
  return content
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Extract variable name without braces
 *
 * @param variable - Variable with braces
 * @returns Variable name only
 */
export function extractVariableName(variable: string): string {
  const match = variable.match(/{{([^}]+)}}/);
  return match ? match[1] : variable;
}

/**
 * Convert variable name to template format
 *
 * @param name - Variable name without braces
 * @returns Variable with braces
 */
export function wrapVariable(name: string): string {
  return `{{${name}}}`;
}

/**
 * Check if string matches variable pattern
 *
 * @param str - String to check
 * @returns True if string is a variable
 */
export function isVariableFormat(str: string): boolean {
  return /^{{[a-zA-Z_][a-zA-Z0-9_]*}}$/.test(str);
}

/**
 * Ensure URL has protocol prefix (https://)
 *
 * SMS URLs are stored without protocol to save characters.
 * Email URLs need the protocol for clickable links.
 *
 * @param url - URL that may or may not have protocol
 * @returns URL with https:// prefix
 *
 * @example
 * ensureUrlProtocol('order.maharaja.net.nz')
 * // Returns: 'https://order.maharaja.net.nz'
 *
 * ensureUrlProtocol('https://order.maharaja.net.nz')
 * // Returns: 'https://order.maharaja.net.nz' (unchanged)
 */
export function ensureUrlProtocol(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  // If empty after trim, return empty
  if (!trimmedUrl) {
    return '';
  }

  // If already has protocol (http:// or https://), return as-is
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  // Otherwise, prepend https://
  return `https://${trimmedUrl}`;
}

/**
 * Batch ensure protocols for multiple URLs
 * Useful for normalizing restaurant URL objects
 *
 * @param urls - Object with URL fields
 * @returns Object with normalized URLs
 *
 * @example
 * ensureUrlProtocols({
 *   ordering_page_url: 'order.maharaja.net.nz',
 *   review_page_url: 'https://review.maharaja.net.nz'
 * })
 * // Returns: {
 * //   ordering_page_url: 'https://order.maharaja.net.nz',
 * //   review_page_url: 'https://review.maharaja.net.nz'
 * // }
 */
export function ensureUrlProtocols<T extends Record<string, any>>(urls: T): T {
  const normalized = { ...urls };

  // List of known URL fields
  const urlFields = [
    'ordering_page_url',
    'review_page_url',
    'google_review_url',
    'facebook_url',
    'instagram_url'
  ];

  urlFields.forEach(field => {
    if (field in normalized && typeof normalized[field] === 'string') {
      normalized[field] = ensureUrlProtocol(normalized[field]);
    }
  });

  return normalized;
}
